import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Button, Input, message, Popover, Select, Space, Tag, Tooltip, Typography } from "antd";
import { SendOutlined } from "@ant-design/icons";
import {
  TbShieldSearch,
  TbAlertCircle,
  TbBroadcast,
  TbEdit,
  TbGripHorizontal,
  TbLoader2,
  TbPaperclip,
} from "react-icons/tb";
import EmailAuditPanel from "components/EmailDashboard/EmailAuditPanel";
import { useEmailAudit } from "hooks/useEmailAudit";
import EmailMessage from "./email-message";
import { ApiService } from "api/api.service";
import { useParams, useSearchParams } from "react-router-dom";

import { EmailAccountConfig, EmailThreadData } from "dtos/email.dto";
import RichTextEditor from "components/RichTextEditor";
import { getErrorMessage, getRiskPlusEmail, mergeArraysUnique, parseEmailAddress } from "utils/functions";
import { useQueryClient } from "@tanstack/react-query";
import EmptyInbox from "components/svgs/EmptyInbox";
import { useResizableComposer } from "./resizable-composer.hook";
import { IconType } from "react-icons";
import { LuReply, LuReplyAll } from "react-icons/lu";
import { Merge } from "types/_utils.types";
import RecipientEditor from "./mail-recipient";
import ChartDownloadModal from "./AprChartModal";
import AttachmentsModal, { allowedExtensions, allowedMimeTypes } from "./attachments";

type MailParticipants = {
  from: string;
  to: string[];
  cc: string[];
  bcc: string[];
};

type ReplyMode = "reply-one" | "reply-all" | "broadcast" | "custom";
type PresetMode = Exclude<ReplyMode, "custom">;

const PRESET_MODES: PresetMode[] = ["reply-one", "reply-all", "broadcast"];

const replyModeProps: Record<ReplyMode, { icon: IconType; label: string }> = {
  "reply-one": { icon: LuReply, label: "Reply" },
  "reply-all": { icon: LuReplyAll, label: "Reply All" },
  broadcast: { icon: TbBroadcast, label: "Broadcast" },
  custom: { icon: TbEdit, label: "Custom" },
};

const getRecipients = (
  mode: ReplyMode,
  sender: string,
  parent: MailParticipants,
  thread: Merge<MailParticipants, { from: string[] }>,
) => {
  let to: string[] = [];
  let cc: string[] = [];
  let bcc: string[] = [];

  const parsed = {
    sender: parseEmailAddress(sender),

    parentFrom: parseEmailAddress(parent.from),
    parentTo: parent.to?.map(parseEmailAddress),
    parentCc: parent.cc?.map(parseEmailAddress),
    parentBcc: parent.bcc?.map(parseEmailAddress),

    threadFrom: thread.from?.map(parseEmailAddress),
    threadTo: thread.to?.map(parseEmailAddress),
    threadCc: thread.cc?.map(parseEmailAddress),
    threadBcc: thread.bcc?.map(parseEmailAddress),
  };

  if (mode === "reply-one" || mode === "reply-all") {
    to = parsed.parentFrom === parsed.sender ? parsed.parentTo : [parsed.parentFrom];
    if (!to.length) to = parsed.parentCc;
  }

  if (mode === "reply-all") {
    cc = parsed.parentCc;
    if (parsed.parentFrom !== parsed.sender) {
      cc.push(...parsed.parentTo);
    }

    bcc = parsed.parentBcc;
  }

  if (mode === "broadcast") {
    to = [parsed.parentFrom, ...parsed.parentTo, ...parsed.threadFrom, ...parsed.threadTo];
    cc = [...parsed.parentCc, ...parsed.threadCc];
    bcc = [...parsed.parentBcc, ...parsed.threadBcc];
  }

  to = mergeArraysUnique(...to).filter((m) => m !== parsed.sender);
  cc = mergeArraysUnique(...cc).filter((m) => ![parsed.sender, ...to].includes(m));
  bcc = mergeArraysUnique(...bcc).filter((m) => ![parsed.sender, ...to, ...cc].includes(m));

  return {
    from: parsed.sender,
    to,
    cc,
    bcc,
  };
};

const EMAIL_MESSAGE_FETCH_LIMIT = 10;

interface EmailDetailProps {
  emailThreadId: string | undefined;
  messageId: string | undefined;
  profile?: EmailAccountConfig;
  toggleNegotiationPanel: () => void;
  isPanelOpen: boolean;
  isInboxPanelOpen: boolean;
  openInboxPanel: () => void;
  closeInboxPanel: () => void;
  updateNegotiationContext: (context: {
    application_id?: string;
    loan_id?: string;
    thread_id?: string;
    message_id?: string;
  }) => void;
}

export default function EmailDetail({
  emailThreadId,
  profile,
  messageId,
  isPanelOpen,
  toggleNegotiationPanel,
  closeInboxPanel,
  openInboxPanel,
  isInboxPanelOpen,
  updateNegotiationContext,
}: Readonly<EmailDetailProps>) {
  const { app_key } = useParams<{ app_key: string }>();
  const { orgCode } = useParams<{ orgCode: string }>();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const chatId = decodeURIComponent(searchParams.get("ncId") || "");
  const replyingTo = decodeURIComponent(searchParams.get("replyTo") || "");
  const chatQuery = ApiService.communication.useGetChatById(chatId);
  const chat = chatQuery.data?.result;

  const getDraftMessageRes = ApiService.email.useGetDraft({ threadId: emailThreadId });
  const draftMessage = getDraftMessageRes.data?.result;
  const updateDraftMessage = ApiService.email.useUpsertDraft();

  const messagesQuery = ApiService.email.useGetThreadMessagesInfinite({
    threadId: emailThreadId,
    appKey: app_key,
    limit: EMAIL_MESSAGE_FETCH_LIMIT,
  });

  const orgEmailQuery = ApiService.email.useGetThreadMessagesInfiniteForOrg({
    threadId: emailThreadId,
    orgCode: orgCode,
    limit: EMAIL_MESSAGE_FETCH_LIMIT,
    profileId: profile?._id,
  });

  const [openAttachment, setOpenAttachment] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const sendReply = ApiService.email.useReplyEmail();
  const deleteDraftMessage = ApiService.email.useDeleteDraft();
  const audit = useEmailAudit(draftMessage?._id);
  // Flatten all pages into a single array of emails
  const emails = useMemo(() => {
    const orderedPages: EmailThreadData[] = [];
    if (messagesQuery.data?.pages?.length) {
      let lastPage = -1;
      for (const result of [...messagesQuery.data.pages].sort((a, b) => b.page - a.page)) {
        if (result.page === lastPage) continue;
        lastPage = result.page;
        orderedPages.push(...result.data);
      }
    }

    return orderedPages;
  }, [messagesQuery.data]);
  const orgEmails = useMemo(() => {
    if (!orgEmailQuery.data?.pages) return [];
    return orgEmailQuery.data.pages.flatMap((page) => page.data || []);
  }, [orgEmailQuery.data]);

  const emailsToUse = app_key ? emails : orgEmails;
  const queryToUse = app_key ? messagesQuery : orgEmailQuery;
  const { hasNextPage: canLoadMore, isFetchingNextPage: loadingMore, fetchNextPage: loadMore } = queryToUse;
  const threadDetails = emailsToUse?.[0]?.thread;
  const fullApplication = ApiService.application.useGetByApplicationId(threadDetails?.application);

  // const getaprChartsRes = ApiService.email.useGetAprCharts(getRiskPlusEmail("46ca-7ad7ad8d"));
  const getaprChartsRes = ApiService.email.useGetAprCharts(
    getRiskPlusEmail(fullApplication.data?.data.application.app_key),
  );
  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);

  const [isAttachModalOpen, setIsAttachModalOpen] = useState(false);
  const [replyingToMessageId, setReplyingToMessageId] = useState<string | null>(null);
  const [chartsModalOpen, setChartsModalOpen] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());
  const [selectedMessageToReply, setSelectedMessageToReply] = useState<EmailThreadData | null>(null);
  const [replySubject, setReplySubject] = useState<string>("");
  const [replyHtml, setReplyHtml] = useState("");
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<string[] | undefined>(undefined);
  const [replyCc, setReplyCc] = useState<string[] | undefined>(undefined);
  const [replyBcc, setReplyBcc] = useState<string[] | undefined>(undefined);
  const [replyMode, setReplyMode] = useState<ReplyMode>("reply-one");
  const [recipientsEditable, setRecipientsEditable] = useState(false);
  const initializedFor = useRef<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const listEndRef = useRef<HTMLDivElement>(null);
  const [sentinelEl, setSentinelEl] = useState<HTMLDivElement | null>(null);
  const prevScrollHeight = useRef(0);

  const loadingMoreRef = useRef(loadingMore);
  loadingMoreRef.current = loadingMore;

  const pageCount = queryToUse.data?.pages.length ?? 0;
  useEffect(() => {
    if (!sentinelEl || !canLoadMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting || loadingMoreRef.current) return;
        prevScrollHeight.current = scrollContainerRef.current?.scrollHeight ?? 0;
        loadMore();
      },
      { root: scrollContainerRef.current, rootMargin: "300px 0px" },
    );

    observer.observe(sentinelEl);
    return () => observer.disconnect();
  }, [sentinelEl, canLoadMore, loadMore, pageCount]);

  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || !prevScrollHeight.current) return;
    el.scrollTop += el.scrollHeight - prevScrollHeight.current;
    prevScrollHeight.current = 0;
  }, [emailsToUse]);

  const applyPreset = (mode: PresetMode, target?: EmailThreadData) => {
    const source = target ?? emailsToUse?.find((m) => m._id === replyingToMessageId);
    if (!source || !profile?.email) return;
    const recipients = getRecipients(mode, profile.email, source, source.thread);
    setReplyTo(recipients.to);
    setReplyCc(recipients.cc);
    setReplyBcc(recipients.bcc);
    setReplyMode(mode);
  };

  useEffect(() => {
    if (!replyingToMessageId) {
      initializedFor.current = null;
      return;
    }
    if (initializedFor.current === replyingToMessageId) return;
    const target = emailsToUse?.find((m) => m._id === replyingToMessageId);
    if (!target || !profile?.email) return;
    initializedFor.current = replyingToMessageId;
    setRecipientsEditable(false);
    applyPreset("reply-one", target);
  }, [replyingToMessageId, emailsToUse, profile?.email]);

  useEffect(() => {
    if (chat?.channel === "email" && chat.body) {
      setReplySubject(chat.subject);
      setReplyHtml(chat.body);
    }
  }, [chat]);

  const composerOpen = Boolean(replyingToMessageId && selectedMessageToReply);
  const { mounted, entered, dragging, panelProps, handleProps } = useResizableComposer({
    open: composerOpen,
    containerRef: shellRef,
    storageKey: "email-composer-height",
  });

  useEffect(() => {
    if (!loadMoreRef.current || !queryToUse.hasNextPage || queryToUse.isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && queryToUse.hasNextPage && !queryToUse.isFetchingNextPage) {
          queryToUse.fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [queryToUse]);

  const scrollToBottom = () => {
    setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 100);
  };

  const handleSaveDraftMessage = () => {
    if (!emailThreadId) return;
    const currentMessage = replyHtml.trim();
    const existingDraft = draftMessage?.message.trim();
    if (currentMessage === existingDraft) return;

    updateDraftMessage.mutate({
      threadId: emailThreadId,
      body: {
        replyMessage: replyHtml,
        subject: replySubject,
        bcc: replyBcc,
        cc: replyCc,
        to: replyTo,
        messageId: replyingToMessageId as unknown as string,
      },
    });
  };

  const handleInsertImageIntoBody = (dataUrl: string, index: number) => {
    const file = attachedFiles[index];
    if (!file) return;
    const imgTag = `<img src="${dataUrl}" alt="${file.name}" style="max-width:100%;" />`;
    setReplyHtml((prev) => prev + imgTag);
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (!replyingToMessageId) {
      setSelectedMessageToReply(null);
      return;
    }
    setSelectedMessageToReply(emailsToUse?.find((m) => m._id === replyingToMessageId) ?? null);
  }, [replyingToMessageId, emailsToUse]);
  useEffect(() => {
    if (draftMessage) {
      setReplyHtml(draftMessage?.replyMessage);
      setReplySubject(draftMessage?.subject);
      setReplyingToMessageId(draftMessage.message ?? null);
    } else {
      setReplyHtml("");
      setReplySubject("");
      setReplyingToMessageId(null);
    }
  }, [draftMessage]);

  useEffect(() => {
    if (!replyingToMessageId) return;
    const target = emailsToUse?.find((m) => m._id === replyingToMessageId);
    if (target) setReplySubject(`Re: ${target.subject || ""}`);
    scrollToBottom();
  }, [replyingToMessageId]);

  useEffect(() => {
    if (!replyingToMessageId || !replyMode || replyMode === "custom" || !profile?.email) return;
    const target = emailsToUse?.find((m) => m._id === replyingToMessageId);
    if (!target) return;
    const recipients = getRecipients(replyMode, profile.email, target, target.thread);
    setReplyTo(recipients.to);
    setReplyCc(recipients.cc);
    setReplyBcc(recipients.bcc);
  }, [replyingToMessageId, replyMode, profile?.email]);

  useEffect(() => {
    if (replyingTo) setReplyingToMessageId(replyingTo);
    else if (emailThreadId) {
      queryClient.invalidateQueries({
        queryKey: ["get-thread-messages"],
      });
      window.scrollTo(0, 0);
      setSelectedMessageToReply(null);
      setReplyingToMessageId(null);
      setAttachedFiles([]);
    }
  }, [replyingTo, emailThreadId, queryClient]);

  // Auto-expand the last message when emails load
  useMemo(() => {
    const defaultOpened = (emailsToUse.find((e) => e._id === messageId) || emailsToUse.at(-1))?._id;
    if (defaultOpened) setExpandedMessages(new Set([defaultOpened]));
  }, [emailsToUse, messageId]);

  if (!emailThreadId) {
    return (
      <Space direction="vertical" className="w-full h-[95vh] items-center flex justify-center">
        <div className="flex flex-1 items-center justify-center size-full text-gray-500">
          <EmptyInbox title="No Mail Selected" subtitle="Select a mail to view all the messages" />
        </div>
        {!isInboxPanelOpen ? <Button onClick={openInboxPanel}>Open Inbox</Button> : null}
      </Space>
    );
  }

  const handleSendReply = () => {
    sendReply.mutate(
      {
        messageId: replyingToMessageId || emailThreadId,
        appKey: app_key || "",
        profileId: profile?._id,
        body: replyHtml,
        subject: replySubject,
        attachments: attachedFiles,
        negotiation_id: chatId,
        to: replyTo,
        cc: replyCc,
        bcc: replyBcc,
      },
      {
        onSuccess: () => {
          setReplyHtml("");
          if (editorRef.current) {
            editorRef.current.innerHTML = "";
          }
          setReplyingToMessageId(null);
          setAttachedFiles([]);
          // setReplySubject("");
          setReplyTo(undefined);
          setReplyCc(undefined);
          setReplyBcc(undefined);
          message.success("Reply sent successfully");
          queryToUse.refetch();
          if (draftMessage?._id) {
            deleteDraftMessage.mutate({
              draftId: draftMessage?._id,
              threadId: emailThreadId,
            });
          }
        },
      },
    );
  };

  const setMessageRef = (id: string) => (el: HTMLDivElement | null) => {
    messageRefs.current[id] = el;
  };

  const handleReplyToMessage = (messageId: string) => {
    setReplyingToMessageId(messageId);
    setReplyMode("reply-one");
  };

  const toggleMessageExpansion = (messageId: string) => {
    setExpandedMessages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });

    setSearchParams((prev) => {
      prev.delete("messageId");
      return prev;
    });
  };

  function onCancelReply() {
    setReplyingToMessageId(null);
    setSelectedMessageToReply(null);
    setReplyTo(undefined);
    setReplyCc(undefined);
    setReplyBcc(undefined);
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      const mismatchedFileTypes = fileArray.filter((file) => {
        // Primary check: MIME type
        if (file.type && allowedMimeTypes.has(file.type)) {
          return false;
        }

        // Fallback: extension check (important for docs/xlsx)
        const ext = file.name.split(".").pop()?.toLowerCase();
        return !ext || !allowedExtensions.has(ext);
      });

      if (mismatchedFileTypes.length > 0) {
        message.error("Only images, PDFs, Word, and Excel files are allowed");
        return;
      }

      const unique = fileArray.filter(
        (f) => !attachedFiles.some((a) => a.name === f.name && a.type === f.type && a.size === f.size),
      );

      if (unique.length === 0) {
        message.error("Files are already selected");
        return;
      }

      const oversizedFiles = unique.filter((file) => file.size > 25 * 1024 * 1024); // 25MB limit

      if (oversizedFiles.length > 0) {
        message.error(`Some files exceed 25MB limit: ${oversizedFiles.map((f) => f.name).join(", ")}`);
        return;
      }

      const available = fileArray.slice(0, 5 - attachedFiles.length);

      setAttachedFiles((prev) => [...prev, ...available]);
      // message.success(`${fileArray.length} file(s) attached`);
    }

    event.target.value = "";
  };

  const handleRemoveFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  if (!emailsToUse) {
    return (
      <div className="flex-1 h-[80vh] flex justify-center items-center">
        <TbLoader2 size={64} className="stroke-purple-600 animate-spin" />
      </div>
    );
  }

  if (queryToUse.isLoading) {
    return (
      <div className="flex-1 w-full flex items-center justify-center h-[80vh]">
        <TbLoader2 size={64} className="stroke-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div ref={shellRef} className="flex h-full flex-1 flex-col overflow-hidden bg-white">
      <div ref={scrollContainerRef} className="min-h-0 flex-1 overflow-y-auto">
        <div ref={setSentinelEl} className="h-px" />

        {queryToUse.isFetchingNextPage && (
          <div className="flex justify-center py-4">
            <TbLoader2 size={24} className="animate-spin stroke-purple-600" />
          </div>
        )}

        {!queryToUse.hasNextPage && emailsToUse.length > EMAIL_MESSAGE_FETCH_LIMIT && (
          <Typography.Text type="secondary" className="block py-3 text-center text-xs">
            Beginning of thread
          </Typography.Text>
        )}

        <div>
          {emailsToUse.map((message, i) => {
            const isExpanded = expandedMessages.has(message._id);
            const showSubject = emailsToUse[i - 1]?.subject !== message.subject;
            const currHighlighted = message._id === replyingToMessageId;
            const nextHighlighted = emailsToUse[i + 1]?._id === replyingToMessageId;

            return (
              <EmailMessage
                key={message._id}
                closeInboxPanel={closeInboxPanel}
                message={message}
                onReply={handleReplyToMessage}
                onCancel={onCancelReply}
                messageRef={setMessageRef(message._id)}
                isExpanded={isExpanded}
                onToggleExpand={() => toggleMessageExpansion(message._id)}
                showSubject={showSubject}
                outgoing={message.from === profile?.email} // INCORRECT
                highlight={currHighlighted}
                topBorder={i === 0 ? false : currHighlighted || showSubject ? "full" : false}
                bottomBorder={
                  message.subject !== emailsToUse[i + 1]?.subject
                    ? false
                    : nextHighlighted
                      ? false
                      : currHighlighted
                        ? "full"
                        : true
                }
                isPanelOpen={isPanelOpen}
                toggleNegotiationPanel={toggleNegotiationPanel}
                isInboxPanelOpen={isInboxPanelOpen}
                openInboxPanel={openInboxPanel}
                updateNegotiationContext={updateNegotiationContext}
              />
            );
          })}
        </div>

        <div ref={listEndRef} />
      </div>

      <div
        {...panelProps}
        className={[
          "flex-shrink-0 pt-2 overflow-hidden",
          dragging ? "" : "transition-[height] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]",
        ].join(" ")}
      >
        {mounted && selectedMessageToReply && (
          <div
            className={[
              "h-full px-12 pb-4 pt-1 transition-opacity duration-200",
              entered ? "opacity-100" : "opacity-0",
            ].join(" ")}
          >
            <div className="relative flex h-full flex-col overflow-hidden rounded-lg border border-solid border-gray-300">
              <div
                {...handleProps}
                role="separator"
                aria-orientation="horizontal"
                className="group absolute inset-x-0 top-0 z-10 flex h-3 touch-none cursor-ns-resize items-center justify-center"
              >
                <TbGripHorizontal
                  size={16}
                  className={[
                    "text-gray-400 transition-opacity",
                    dragging ? "opacity-100" : "opacity-0 group-hover:opacity-100",
                  ].join(" ")}
                />
              </div>

              <div className="flex flex-shrink-0 items-start justify-between gap-3 border-0 border-b border-solid border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-700">
                <RecipientEditor
                  editable={recipientsEditable}
                  value={{ to: replyTo ?? [], cc: replyCc ?? [], bcc: replyBcc ?? [] }}
                  onChange={(next) => {
                    setReplyTo(next.to);
                    setReplyCc(next.cc);
                    setReplyBcc(next.bcc);
                    setReplyMode("custom");
                  }}
                />

                <Space className="shrink-0" direction="vertical">
                  <Tooltip title="Customize recipient list">
                    <Select<ReplyMode>
                      value={replyMode}
                      onOpenChange={(open) => {
                        if (open) setRecipientsEditable(true);
                      }}
                      onSelect={(mode) => {
                        if (mode !== "custom") applyPreset(mode);
                      }}
                      variant="borderless"
                      size="small"
                      suffixIcon={null}
                      popupMatchSelectWidth={132}
                      styles={{ popup: { root: { padding: 4 } } }}
                      classNames={{
                        popup: {
                          root: "[&_.ant-select-item]:min-h-0 [&_.ant-select-item]:px-3 [&_.ant-select-item]:py-2 [&_.ant-select-item]:text-[13px] [&_.ant-select-item]:leading-5 [&_.ant-select-item]:rounded",
                        },
                      }}
                      options={[
                        ...PRESET_MODES.map((mode) => ({
                          value: mode,
                          label: (
                            <span className="flex items-center gap-1.5">
                              {(() => {
                                const Icon = replyModeProps[mode].icon;
                                return <Icon size={14} />;
                              })()}
                              <span className="truncate">{replyModeProps[mode].label}</span>
                            </span>
                          ),
                        })),
                        ...(replyMode === "custom"
                          ? [
                              {
                                value: "custom" as ReplyMode,
                                disabled: true,
                                label: (
                                  <span className="flex items-center gap-1.5">
                                    <TbEdit size={14} />
                                    <span className="truncate">Custom</span>
                                  </span>
                                ),
                              },
                            ]
                          : []),
                      ]}
                    />
                  </Tooltip>
                </Space>
              </div>

              <Input
                value={replySubject}
                onChange={(e) => setReplySubject(e.target.value)}
                variant="borderless"
                placeholder="Type your subject..."
                className="my-2 flex-shrink-0"
                suffix={
                  <Popover
                    content={
                      <Space direction="vertical">
                        <Space.Compact className="items-center">
                          <TbAlertCircle className="mr-1" size={14} />
                          <Typography.Text>Changing the subject may create duplicate thread!</Typography.Text>
                        </Space.Compact>

                        <Button
                          className="block"
                          size="small"
                          onClick={() => setReplySubject(`Re: ${selectedMessageToReply.subject}`)}
                        >
                          Revert?
                        </Button>
                      </Space>
                    }
                  >
                    {replySubject === `Re: ${selectedMessageToReply.subject}` ? (
                      <span />
                    ) : (
                      <TbAlertCircle color="orange" size={16} />
                    )}
                  </Popover>
                }
              />

              <div className="min-h-0 flex-1 overflow-y-auto p-3 pt-0">
                <RichTextEditor
                  value={replyHtml}
                  onChange={setReplyHtml}
                  autoSave={() => {
                    handleSaveDraftMessage();
                  }}
                  debounceDuration={500}
                />
              </div>

              <div className="flex flex-shrink-0 items-center gap-4 justify-between border-0 border-t border-solid border-gray-200 bg-gray-50 px-4 py-4">
                <input type="file" id="file-input" multiple onChange={handleFileSelect} className="hidden" />
                <Button icon={<TbPaperclip size={16} />} onClick={() => setIsAttachModalOpen(true)}>
                  Attachments {attachedFiles.length ? <Tag>{attachedFiles.length}</Tag> : null}
                </Button>
                <AttachmentsModal
                  open={isAttachModalOpen}
                  onClose={() => setIsAttachModalOpen(false)}
                  attachedFiles={attachedFiles}
                  onRemoveFile={handleRemoveFile}
                  onUploadClick={() => document.getElementById("file-input")?.click()}
                  isFileLimitReached={attachedFiles.length >= 5}
                  onOpenChartsPicker={() => setChartsModalOpen(true)}
                  chartsDisabled={
                    !threadDetails.application ||
                    !getaprChartsRes.data?.result?.charts?.length ||
                    attachedFiles.length >= 5
                  }
                  chartsError={getaprChartsRes.isError ? getErrorMessage(getaprChartsRes.error) : undefined}
                  onInsertImageIntoBody={handleInsertImageIntoBody}
                />

                <Space>
                  <Button type="default" onClick={onCancelReply}>
                    Cancel
                  </Button>
                  <Tooltip title="Check email against policies">
                    <Button
                      type="default"
                      variant="outlined"
                      disabled={updateDraftMessage.isPending || !replyHtml.trim()}
                      onClick={() => audit.analyze(replyHtml, replySubject, orgCode, draftMessage?._id)}
                      className=" hover:bg-purple-50 hover:border-purple-300 hover:text-purple-700"
                      style={audit.isOpen ? { color: "#6d28d9", borderColor: "#c4b5fd", background: "#f5f3ff" } : {}}
                      icon={<TbShieldSearch className="text-base" />}
                    >
                      Analyze
                    </Button>
                  </Tooltip>
                  <Button
                    onClick={handleSendReply}
                    type="primary"
                    disabled={!replyHtml.trim() || !replySubject.trim() || audit.isOpen}
                    loading={sendReply.isPending}
                  >
                    Send
                    <SendOutlined className="text-xs" />
                  </Button>
                </Space>
              </div>
            </div>
          </div>
        )}
      </div>
      <EmailAuditPanel
        isOpen={audit.isOpen}
        isAnalyzing={audit.isAnalyzing}
        auditResult={audit.auditResult?.result}
        isFixing={audit.isFixing}
        fixResult={audit.fixResult?.result}
        selected={audit.selected}
        onToggle={audit.toggleSelected}
        onFix={() => audit.fix(draftMessage?._id)}
        onDiscardFix={audit.discardFix}
        onAcceptFix={(subject, body) => {
          setReplySubject(subject);
          // The fix API returns plain text. Wrap in <p> so the rich text editor
          // displays it correctly. Replace this if your editor has a plain-text setter.
          setReplyHtml(`<p>${body}</p>`);
          audit.close();
        }}
        onClose={audit.close}
      />

      <ChartDownloadModal
        open={chartsModalOpen}
        onClose={() => setChartsModalOpen(false)}
        charts={getaprChartsRes.data?.result.charts ?? []}
        applicationId={getRiskPlusEmail(fullApplication.data?.application.app_key) as string}
        onAttach={(files, selectedAvailable) => {
          setAttachedFiles((prev) => [...prev, ...files]);
          // close only if everything succeeded — errors stay visible so user knows
          if (files.length === selectedAvailable) {
            setChartsModalOpen(false);
          }
        }}
      />
    </div>
  );
}
